import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  MessageSquare, Send, Search, ArrowLeft, AlertTriangle, PhoneCall,
  Loader2, QrCode, Archive, ShieldCheck, MailOpen
} from "lucide-react";
import AppShell from "../components/app/AppShell";
import ConversationProtection from "../components/app/ConversationProtection";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useInbox } from "../context/InboxContext";
import { useI18n } from "../i18n";

/**
 * RC-WEB-4 §3/§15 — THE canonical inbox. There is no second "Privados" inbox.
 * RC-WEB-5 GATE 4/5/16 — premium empty-detail state, unread expressed ALWAYS
 * in unread messages, fully localized.
 */

const API = "/api";

const Messages = () => {
  const { conversations, unread, loading, refresh } = useInbox();
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("c");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const FILTERS = [
    { id: "all", label: t("app.inbox.filter_all") },
    { id: "unread", label: t("app.inbox.filter_unread") },
    { id: "urgent", label: t("app.inbox.filter_urgent") },
  ];

  const relative = useCallback((iso) => {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return t("app.inbox.now");
    if (m < 60) return t("app.inbox.min_ago", { n: m });
    const h = Math.floor(m / 60);
    if (h < 24) return t("app.inbox.hour_ago", { n: h });
    const d = Math.floor(h / 24);
    if (d < 7) return t("app.inbox.day_ago", { n: d });
    return new Date(iso).toLocaleDateString();
  }, [t]);

  const loadDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/inbox/conversations/${id}`);
      setDetail(res.data);
      refresh();
    } catch (_e) {
      toast.error(t("app.inbox.open_error"));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [refresh, t]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  const select = (id) => setParams(id ? { c: id } : {});

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return conversations.filter((c) => {
      if (filter === "unread" && !c.unread) return false;
      if (filter === "urgent" && !c.urgent) return false;
      if (!q) return true;
      return `${c.profile_name} ${c.last_message}`.toLowerCase().includes(q);
    });
  }, [conversations, filter, query]);

  const sendReply = async () => {
    if (!reply.trim() || !detail) return;
    setSending(true);
    try {
      await axios.post(`${API}/chat/${detail.id}/message`, { message: reply.trim() });
      setReply("");
      setSendFailed(false);
      await loadDetail(detail.id);
    } catch (e) {
      setSendFailed(true);
      toast.error(e.response?.data?.detail === "message_not_allowed"
        ? t("app.inbox.send_blocked")
        : t("app.inbox.send_error"));
    } finally {
      setSending(false);
    }
  };

  const list = (
    <div className="flex flex-col gap-3" data-testid="conversation-list">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("app.inbox.search")}
            className="h-11 rounded-xl border-slate-300 bg-white pl-9 text-[#0B2545]"
            data-testid="inbox-search-input"
          />
        </div>
        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`inbox-filter-${f.id}`}
              aria-pressed={filter === f.id}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                filter === f.id
                  ? "bg-[#0B2545] text-white"
                  : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading && conversations.length === 0 ? (
        <div className="flex justify-center py-16" data-testid="inbox-loading">
          <Loader2 className="h-6 w-6 animate-spin text-[#1B84FF]" />
        </div>
      ) : visible.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center"
          data-testid="inbox-empty-state"
        >
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
            <MessageSquare className="h-8 w-8 text-slate-600" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-[#0B2545]">
            {conversations.length === 0
              ? t("app.inbox.empty_title")
              : query.trim()
              ? t("app.inbox.empty_search_title")
              : t("app.inbox.empty_filter_title")}
          </h3>
          <p className="mx-auto mb-6 max-w-md text-sm text-slate-600">
            {conversations.length === 0
              ? t("app.inbox.empty_text")
              : query.trim()
              ? t("app.inbox.empty_search_text", { query: query.trim() })
              : t("app.inbox.empty_filter_text")}
          </p>
          {conversations.length === 0 && (
            <Link to="/dashboard/vehicles">
              <Button className="bg-[#1B84FF] text-white hover:bg-[#0F6FDB]" data-testid="inbox-empty-qr-btn">
                <QrCode className="mr-2 h-4 w-4" /> {t("app.inbox.view_qr")}
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <ul className="divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {visible.map((c) => (
            <li key={c.id}>
              <button
                onClick={() => select(c.id)}
                data-testid={`conversation-item-${c.id}`}
                className={`flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors hover:bg-slate-50 ${
                  selectedId === c.id ? "bg-[#1B84FF]/5" : ""
                }`}
              >
                <div
                  className={`mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                    c.urgent ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {c.urgent ? <AlertTriangle className="h-5 w-5" /> : <MessageSquare className="h-5 w-5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-[#0B2545]">
                      {c.profile_name || t("app.inbox.protection")}
                    </span>
                    {c.role === "trusted" && (
                      <span className="rounded-full border border-slate-300 px-1.5 text-[10px] font-semibold text-slate-700">
                        {t("app.inbox.trusted_badge")}
                      </span>
                    )}
                    {c.read_only && (
                      <span className="flex items-center gap-1 rounded-full border border-slate-300 px-1.5 text-[10px] font-semibold text-slate-700">
                        <Archive className="h-3 w-3" /> {t("app.inbox.historic")}
                      </span>
                    )}
                    <span className="ml-auto shrink-0 text-[11px] text-slate-600">{relative(c.last_activity)}</span>
                  </div>
                  <p className={`mt-0.5 truncate text-sm ${c.unread ? "font-semibold text-[#0B2545]" : "text-slate-600"}`}>
                    {c.is_call_request && <PhoneCall className="mr-1 inline h-3.5 w-3.5 text-[#1B84FF]" />}
                    {c.last_message || t("app.inbox.no_messages")}
                  </p>
                </div>
                {c.unread > 0 && (
                  <span
                    className="mt-1 min-w-[20px] rounded-full bg-red-600 px-1.5 text-center text-[11px] font-bold text-white"
                    data-testid={`conversation-unread-${c.id}`}
                    title={t("app.inbox.unread_n", { n: c.unread })}
                  >
                    {c.unread}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  /* GATE 4 — compact, premium empty-detail state (no giant blank rectangle). */
  const emptyDetail = (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-12 text-center"
         data-testid="conversation-empty-detail">
      <span className="flex h-20 w-20 items-center justify-center rounded-3xl bg-[#0B2545]">
        <MailOpen className="h-9 w-9 text-[#5BC0FF]" />
      </span>
      <div>
        <h3 className="text-base font-bold text-[#0B2545]">{t("app.inbox.select_title")}</h3>
        <p className="mx-auto mt-1 max-w-sm text-sm text-slate-600">{t("app.inbox.select_text")}</p>
      </div>
      <p className="mx-auto flex max-w-sm items-start gap-2 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-700">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#1B84FF]" />
        {t("app.inbox.select_privacy")}
      </p>
      <Link to="/dashboard/vehicles">
        <Button variant="outline" size="sm" className="border-slate-300 text-[#0B2545]" data-testid="conversation-empty-qr-btn">
          <QrCode className="mr-2 h-4 w-4" /> {t("app.inbox.view_qr")}
        </Button>
      </Link>
    </div>
  );

  const conversation = (
    <div className="flex h-full min-h-[420px] flex-col rounded-2xl border border-slate-200 bg-white" data-testid="conversation-panel">
      {!selectedId ? emptyDetail
      : detailLoading && !detail ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#1B84FF]" />
        </div>
      ) : !detail ? (
        <div className="flex flex-1 items-center justify-center p-8 text-sm text-slate-600">
          {t("app.inbox.not_available")}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3">
            <button
              onClick={() => select(null)}
              className="rounded-lg p-1.5 text-slate-700 hover:bg-slate-100 lg:hidden"
              data-testid="conversation-back-btn"
              aria-label={t("app.back")}
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-[#0B2545]" data-testid="conversation-title">
                {detail.profile_name || t("app.inbox.protection")}
              </p>
              <p className="text-xs text-slate-600">
                {detail.status === "blocked"
                  ? t("app.protection.blocked_badge")
                  : detail.read_only ? t("app.inbox.closed_state") : t("app.inbox.open_state")}
                {detail.urgent ? ` · ${t("app.inbox.urgent")}` : ""}
              </p>
            </div>
            {(detail.status === "blocked" || !detail.read_only) && (
              <ConversationProtection detail={detail} onDone={() => { loadDetail(detail.id); refresh(); }} />
            )}
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="conversation-messages">
            {detail.messages.map((m) => {
              const mine = m.sender === "owner";
              const system = m.message_type === "system";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm ${
                      system
                        ? "border border-amber-300 bg-amber-50 text-amber-900"
                        : mine
                        ? "bg-[#1B84FF] text-white"
                        : "bg-slate-100 text-[#0B2545]"
                    }`}
                    data-testid={`message-bubble-${m.id}`}
                  >
                    <p className="whitespace-pre-wrap">{m.message}</p>
                    <p className={`mt-1 text-[10px] ${mine && !system ? "text-white/80" : "text-slate-600"}`}>
                      {relative(m.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {detail.can_reply ? (
            <div className="border-t border-slate-200 p-3">
              <div className="flex items-end gap-2">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={2}
                  placeholder={t("app.inbox.reply_placeholder")}
                  className="flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm text-[#0B2545] outline-none focus:border-[#1B84FF]"
                  data-testid="conversation-reply-input"
                />
                <Button
                  onClick={sendReply}
                  disabled={sending || !reply.trim()}
                  className="h-11 bg-[#1B84FF] text-white hover:bg-[#0F6FDB]"
                  data-testid="conversation-send-btn"
                  aria-label={t("app.inbox.reply_placeholder")}
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              {sendFailed && (
                <button
                  onClick={sendReply}
                  className="mt-2 text-xs font-semibold text-red-700 hover:underline"
                  data-testid="conversation-retry-btn"
                >
                  {t("app.retry")}
                </button>
              )}
            </div>
          ) : (
            <div className="border-t border-slate-200 p-3 text-center text-xs text-slate-700" data-testid="conversation-readonly-note">
              {detail.source === "legacy"
                ? t("app.inbox.readonly_legacy")
                : detail.status === "blocked"
                ? t("app.protection.readonly_blocked")
                : t("app.inbox.readonly_closed")}
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <AppShell
      title={t("app.inbox.title")}
      subtitle={unread > 0
        ? (unread === 1 ? t("app.inbox.unread_1") : t("app.inbox.unread_n", { n: unread }))
        : t("app.inbox.all_clear")}
      maxWidth="max-w-6xl"
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <div className={selectedId ? "hidden lg:block" : "block"}>{list}</div>
        <div className={selectedId ? "block" : "hidden lg:block"}>{conversation}</div>
      </div>
    </AppShell>
  );
};

export default Messages;
